import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  Chip,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Logo from '../../components/common/Logo';
import { offerDetails } from '../../services/api';
import QRCode from 'react-qr-code';

interface OfferData {
  id: number;
  title: string;
  image: string;
  price: string;
}

interface TrackOfferResponse {
  id: number;
  orderId?: string;
  uniqueId?: string;
  title: string;
  description?: string;
  price: number;
  image: string;
  status: string;
  buyerStatus: string;
  paymentStatus?: string;
  buyerNextStep?: string;
  buyerId?: number;
  buyerQrCode?: string;
  seller?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    deletedAt?: string | null;
  };
  buyer?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  locker?: {
    id: number;
    lockerId: string;
    containerId: number;
    status: string;
    availability: string;
    statusExpireAt: string | null;
    createdAt: string;
    price: number;
    offerId?: number;
  };
  lockerHistory?: {
    id: number;
    userId: number;
    offerId: number;
    paymentHistoryId: number;
    lockerId: number;
    lockerRequestId: number;
    timeDuration: string;
    bookedAt: string;
    lockerRequestedBy: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  history?: Array<{
    step: string;
    date: string;
  }>;
  timeDuration?: string;
  bookedAt?: string;
  commentHistory?: any[];
}

interface Tokens {
  accessToken?: string;
  refreshToken?: string;
}

interface MasterData {
  maxOfferAcceptanceTime?: string;
  maxItemSubmitTime?: string;
  extendLockerDurarionBefore?: string;
}

interface SatisfiedData {
  status?: string;
  pickupTime?: string;
  expiredIn?: string | number;
}

interface TrackOfferResponse {
  id: number;
  orderId?: string;
  uniqueId?: string;
  title: string;
  description?: string;
  price: number;
  image: string;
  status: string;
  buyerStatus: string;
  paymentStatus?: string;
  buyerNextStep?: string;
  buyerId?: number;
  buyerQrCode?: string;
  seller?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    deletedAt?: string | null;
  };
  buyer?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  locker?: {
    id: number;
    lockerId: string;
    containerId: number;
    status: string;
    availability: string;
    statusExpireAt: string | null;
    createdAt: string;
    price: number;
    offerId?: number;
  };
  lockerHistory?: {
    id: number;
    userId: number;
    offerId: number;
    paymentHistoryId: number;
    lockerId: number;
    lockerRequestId: number;
    timeDuration: string;
    bookedAt: string;
    lockerRequestedBy: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  history?: Array<{
    step: string;
    date: string;
  }>;
  timeDuration?: string;
  bookedAt?: string;
  commentHistory?: any[];
}

const BuyerOfferDetail: React.FC = () => {
  const { offerCode } = useParams<{ offerCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [trackOfferData, setTrackOfferData] = useState<TrackOfferResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState<Tokens | undefined>(undefined);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [masterData, setMasterData] = useState<MasterData>({});
  const [satisfiedData, setSatisfiedData] = useState<SatisfiedData | null>(null);
  const [canExtend, setCanExtend] = useState<boolean>(false);
  const [loadingCancel, setLoadingCancel] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Dialog states
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [lockerLocationDialogOpen, setLockerLocationDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Time calculation functions extracted from React Native
  const getTimeRemaining = (
    durationMinutes: string | number | null | undefined,
    startTime: string | null | undefined,
  ): string => {
    if (!durationMinutes || !startTime) {
      return '';
    }

    const startTimestamp = new Date(startTime).getTime();
    const durationMs = Number(durationMinutes) * 60 * 1000;
    const expiryTimestamp = startTimestamp + durationMs;
    const timeLeft = Math.max(0, expiryTimestamp - currentTime);

    if (timeLeft <= 0) {
      return 'Expired';
    }

    const totalSeconds = Math.floor(timeLeft / 1000);
    const hours = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, '0');
    const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(totalSeconds % 60)
      .toString()
      .padStart(2, '0');

    return `${hours}h:${minutes}m:${seconds}s`;
  };

  const getHistoryStepDate = (step: string) => {
    if (!trackOfferData?.history) {
      return null;
    }
    const entry = trackOfferData.history.find(
      (item: any) => item?.step?.toUpperCase() === step.toUpperCase(),
    );
    return entry?.date || null;
  };

  const formatNextStepMessage = (
    message: string | null | undefined,
  ): {
    beforeText: string;
    afterText: string;
    timeValue: string;
    showTime: boolean;
  } => {
    if (!message) {
      return {
        beforeText: 'No further action required.',
        afterText: '',
        timeValue: '',
        showTime: false,
      };
    }

    if (!message.includes('%TIME%')) {
      return {
        beforeText: message,
        afterText: '',
        timeValue: '',
        showTime: false,
      };
    }

    let timeValue = '';
    let showTime = true;

    // For OFFER_SENT status
    if (
      trackOfferData?.status === 'OFFER_SENT' &&
      masterData?.maxOfferAcceptanceTime
    ) {
      const startTime = getHistoryStepDate('OFFER_SENT');
      if (startTime) {
        timeValue = getTimeRemaining(
          masterData.maxOfferAcceptanceTime,
          startTime,
        );
        if (timeValue === 'Expired' || timeValue === '') {
          showTime = false;
          timeValue = '';
        }
      }
    }
    // For BUYER_PICKED_ITEM use satisfied timer
    else if (
      trackOfferData?.status === 'BUYER_PICKED_ITEM' &&
      satisfiedData?.pickupTime &&
      satisfiedData?.expiredIn
    ) {
      timeValue = getTimeRemaining(
        satisfiedData?.expiredIn,
        satisfiedData?.pickupTime,
      );
      if (timeValue === 'Expired' || timeValue === '') {
        showTime = false;
        timeValue = '';
      }
    }
    // For BUYER_UNSATISFIED - first try locker timing, if expired use maxItemSubmitTime
    else if (trackOfferData?.status === 'BUYER_UNSATISFIED') {
      if (trackOfferData?.timeDuration && trackOfferData?.bookedAt) {
        timeValue = getTimeRemaining(
          trackOfferData.timeDuration,
          trackOfferData.bookedAt,
        );
      }

      if (timeValue === 'Expired' || timeValue === '') {
        if (masterData?.maxItemSubmitTime) {
          const startTime = getHistoryStepDate('BUYER_UNSATISFIED');
          if (startTime) {
            timeValue = getTimeRemaining(
              masterData?.maxItemSubmitTime,
              startTime,
            );
            if (timeValue === 'Expired' || timeValue === '') {
              showTime = false;
              timeValue = '';
            }
          } else {
            showTime = false;
            timeValue = '';
          }
        } else {
          showTime = false;
          timeValue = '';
        }
      }
    }
    else if (trackOfferData?.status === 'BUYER_EXTENDED_LOCKER_TIME_FOR_PLACE_ITEM') {
      if (trackOfferData?.timeDuration && trackOfferData?.bookedAt) {
        timeValue = getTimeRemaining(
          trackOfferData.timeDuration,
          trackOfferData.bookedAt,
        );
      }

      if (timeValue === 'Expired' || timeValue === '') {
        if (masterData?.maxItemSubmitTime) {
          const startTime = getHistoryStepDate('BUYER_EXTENDED_LOCKER_TIME_FOR_PLACE_ITEM');
          if (startTime) {
            timeValue = getTimeRemaining(
              masterData?.maxItemSubmitTime,
              startTime,
            );
            if (timeValue === 'Expired' || timeValue === '') {
              showTime = false;
              timeValue = '';
            }
          } else {
            showTime = false;
            timeValue = '';
          }
        } else {
          showTime = false;
          timeValue = '';
        }
      }
    }
    else if (trackOfferData?.status === 'SELLER_EXTENDED_LOCKER_TIME_FOR_TAKE_ITEM') {
      if (trackOfferData?.timeDuration && trackOfferData?.bookedAt) {
        timeValue = getTimeRemaining(
          trackOfferData.timeDuration,
          trackOfferData.bookedAt,
        );
      }

      if (timeValue === 'Expired' || timeValue === '') {
        if (masterData?.maxItemSubmitTime) {
          const startTime = getHistoryStepDate('SELLER_EXTENDED_LOCKER_TIME_FOR_TAKE_ITEM');
          if (startTime) {
            timeValue = getTimeRemaining(
              masterData?.maxItemSubmitTime,
              startTime,
            );
            if (timeValue === 'Expired' || timeValue === '') {
              showTime = false;
              timeValue = '';
            }
          } else {
            showTime = false;
            timeValue = '';
          }
        } else {
          showTime = false;
          timeValue = '';
        }
      }
    }
    // For SELLER_PLACED_ITEM and BUYER_PLACED_ITEM
    else if (
      ['SELLER_PLACED_ITEM', 'BUYER_PLACED_ITEM','BUYER_EXTENDED_LOCKER_TIME'].includes(
        trackOfferData?.status || '',
      ) &&
      trackOfferData?.timeDuration &&
      trackOfferData?.bookedAt
    ) {
      timeValue = getTimeRemaining(
        trackOfferData.timeDuration,
        trackOfferData.bookedAt,
      );
      if (timeValue === 'Expired' || timeValue === '') {
        showTime = false;
        timeValue = '';
      }
    }

    const parts = message.split('%TIME%');
    const beforeText = parts[0] ?? '';
    const afterText = parts.slice(1).join('%TIME%');

    if (!showTime) {
      return {
        beforeText: beforeText + afterText,
        afterText: '',
        timeValue: '',
        showTime: false,
      };
    }

    return {
      beforeText,
      afterText,
      timeValue: timeValue || '00h:00m:00s',
      showTime: true,
    };
  };

  const formatPaymentStatus = (status: string | undefined): string => {
    if (!status) return '';
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  const getBuyerActions = (orderStatus: string | undefined): string[] => {
    if (!orderStatus) {
      return [];
    }

    const normalizedStatus = orderStatus.toUpperCase();

    switch (normalizedStatus) {
      case 'OFFER_SENT':
        return ['OFFER_SENT'];
      case 'PAYMENT_COMPLETED':
      case 'PAYMENT_IN_ESCROW':
      case 'SELLER_BOOKED_LOCKER':
      case 'SELLER_EXTENDED_LOCKER_TIME':
      case 'BUYER_UNSATISFIED':
        return ['CANCEL_OFFER'];
      case 'OFFER_DECLINED':
        return ['DELETE_OFFER'];
      case 'SELLER_PLACED_ITEM':
      case 'BUYER_EXTENDED_LOCKER_TIME':
        return ['VIEW_LOCKER', 'VIEW_QR_CODE', 'EXTEND_LOCKER_TIME'];
      case 'BUYER_PICKED_ITEM':
        return ['SATISFIED_WITH_OFFER', 'NOT_SATISFIED_WITH_OFFER'];
      case 'BUYER_SATISFIED':
        return ['DELETE_OFFER'];
      case 'SELLER_PICKED_ITEM':
      case 'SUPPORT_PICKED_ITEM':
      case 'ITEM_PICKED_BY_SELLER_FROM_SUPPORT':
      case 'ITEM_PICKED_BY_BUYER_FROM_SUPPORT':
      case 'OFFER_CREATED':
      default:
        return [];
    }
  };

  const canCancelOffer = (status: string | undefined): boolean => {
    if (!status) return false;
    const cancelStatuses = [
      'PAYMENT_COMPLETED',
      'PAYMENT_IN_ESCROW',
      'SELLER_BOOKED_LOCKER',
      'SELLER_EXTENDED_LOCKER_TIME',
      'BUYER_UNSATISFIED',
    ];
    return cancelStatuses.includes(status.toUpperCase());
  };

  const shouldShowExtendTime = (): boolean => {
    if (!trackOfferData) return false;
    
    const validStatuses = [
      'SELLER_PLACED_ITEM',
      'BUYER_UNSATISFIED',
      'BUYER_EXTENDED_LOCKER_TIME_FOR_TAKE_ITEM',
      'BUYER_EXTEND_LOCKER_TIME_FOR_TAKE_ITEM',
      'BUYER_EXTENDED_LOCKER_TIME_FOR_PLACE_ITEM',
      'BUYER_EXTENDED_LOCKER_TIME',
    ];
    
    return (
      validStatuses.includes(trackOfferData.status) &&
      !!trackOfferData.locker?.id &&
      !!trackOfferData.buyerQrCode &&
      trackOfferData.buyerQrCode.trim() !== '' &&
      canExtend
    );
  };

  const shouldShowLockerDetails = (): boolean => {
    if (!trackOfferData) return false;
    return (
      !!trackOfferData.locker?.offerId &&
      trackOfferData.id === trackOfferData.locker.offerId &&
      trackOfferData.status !== 'SELLER_BOOKED_LOCKER'
    );
  };

  const isOrderInProgress = (status: string | undefined): boolean => {
    if (!status) return false;
    const inProgressStatuses = [
      'PAYMENT_COMPLETED',
      'PAYMENT_IN_ESCROW',
      'SELLER_BOOKED_LOCKER',
      'SELLER_EXTENDED_LOCKER_TIME',
      'SELLER_PLACED_ITEM',
      'BUYER_EXTENDED_LOCKER_TIME',
      'BUYER_PICKED_ITEM',
      'BUYER_UNSATISFIED',
      'BUYER_EXTENDED_LOCKER_TIME_FOR_TAKE_ITEM',
      'BUYER_EXTEND_LOCKER_TIME_FOR_TAKE_ITEM',
      'BUYER_EXTENDED_LOCKER_TIME_FOR_PLACE_ITEM',
      'SELLER_EXTEND_LOCKER_TIME_FOR_TAKE_ITEM',
      'SELLER_EXTENDED_LOCKER_TIME_FOR_TAKE_ITEM'
    ];
    return inProgressStatuses.includes(status.toUpperCase());
  };

  const shouldShowQRCode = (): boolean => {
    if (!trackOfferData) return false;
    
    const actions = getBuyerActions(trackOfferData.status);
    const hasViewQRAction = actions.includes('VIEW_QR_CODE');
    const hasExtendedStatus = [
      'SELLER_EXTEND_LOCKER_TIME_FOR_TAKE_ITEM',
      'SELLER_EXTENDED_LOCKER_TIME_FOR_TAKE_ITEM',
    ].includes(trackOfferData.status);
    
    return (
      (hasViewQRAction || hasExtendedStatus) &&
      !!trackOfferData.buyerQrCode &&
      trackOfferData.buyerQrCode.trim() !== ''
    );
  };

  const shouldShowNewQRCodeButton = (): boolean => {
    if (!trackOfferData) return false;
    return (
      trackOfferData.id === trackOfferData.locker?.offerId &&
      (trackOfferData.status === 'BUYER_UNSATISFIED' ||
       trackOfferData.status === 'BUYER_EXTENDED_LOCKER_TIME_FOR_PLACE_ITEM')
    );
  };

  const handleCancelOffer = async () => {
    setCancelDialogOpen(true);
  };

  const confirmCancelOffer = async () => {
    if (!trackOfferData) return;
    
    setLoadingCancel(true);
    setApiError(null);
    
    try {
      // Call API to cancel offer - will need to implement this API endpoint
      // For now, just navigate back
      setCancelDialogOpen(false);
      navigate(`/offer/${offerCode}/tracking`);
    } catch (err: any) {
      setApiError(err?.response?.data?.message || err?.message || 'Failed to cancel offer');
    } finally {
      setLoadingCancel(false);
    }
  };

  // Update timer every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate canExtend based on locker history and master data
  useEffect(() => {
    if (trackOfferData?.lockerHistory?.bookedAt && trackOfferData?.lockerHistory?.timeDuration) {
      const start = new Date(trackOfferData.lockerHistory.bookedAt).getTime();
      const end = start + Number(trackOfferData.lockerHistory.timeDuration) * 60 * 1000;
      
      const extendLockerDurationBefore = masterData?.extendLockerDurarionBefore 
        ? Number(masterData.extendLockerDurarionBefore) 
        : 0;
      
      const cutoffTime = end - (extendLockerDurationBefore * 60 * 1000);
      const current = Date.now();
      
      setCanExtend(current < cutoffTime);
    } else {
      setCanExtend(false);
    }
  }, [trackOfferData?.lockerHistory, masterData?.extendLockerDurarionBefore, currentTime]);

  // Fetch master data for extension timing
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        // Will need to implement this API endpoint
        // For now, use default values
        setMasterData({
          extendLockerDurarionBefore: '15', // Default 15 minutes before expiry
        });
      } catch (err) {
        console.error('Error fetching master data:', err);
      }
    };
    
    fetchMasterData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!offerCode) {
        return;
      }

      // Get data from location state
      if (location.state?.offerData) {
        setOfferData(location.state.offerData);
      }
      if (location.state?.tokens) {
        setTokens(location.state.tokens);
      }
      if (location.state?.trackOfferData) {
        const trackData = location.state.trackOfferData;
        setTrackOfferData(trackData);
        if (!location.state?.offerData) {
          setOfferData({
            id: trackData.id,
            title: trackData.title,
            image: trackData.image,
            price: String(trackData.price)
          });
        }
        setLoading(false);
        return;
      }

      // If trackOfferData is not in state, check if offerCode is base64 of email|trackingId
      let decodedEmail = '';
      let decodedTrackingId = '';
      let isBase64Offer = false;

      try {
        const decoded = atob(offerCode);
        if (decoded.includes('|')) {
          const parts = decoded.split('|');
          if (parts.length === 2 && parts[0].includes('@')) {
            decodedEmail = parts[0];
            decodedTrackingId = parts[1];
            isBase64Offer = true;
          }
        }
      } catch (e) {
        // Not base64 encoded or parsing failed
      }

      if (isBase64Offer) {
        try {
          const response = await offerDetails.trackOffer(decodedEmail, decodedTrackingId, '');
          if (response.success) {
            setTrackOfferData(response.data);
            if (!location.state?.offerData) {
              setOfferData({
                id: response.data.id,
                title: response.data.title,
                image: response.data.image,
                price: String(response.data.price)
              });
            }
            setLoading(false);
            return;
          } else {
            setApiError(response.message || 'Failed to track offer');
          }
        } catch (err: any) {
          const errorMessage = err?.response?.data?.message || err?.message || 'Failed to track offer';
          setApiError(errorMessage);
        }
      }

      setLoading(false);
    };

    fetchData();
  }, [offerCode, location.state]);

  const handleBack = () => {
    navigate(`/offer/${offerCode}`);
  };

  const handleProceedToPayment = () => {
    navigate(`/offer/${offerCode}/payment`, { 
      state: { 
        offerData,
        tokens 
      } 
    });
  };

  const nextStepMessage = formatNextStepMessage(trackOfferData?.buyerNextStep || null);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Logo variant="full" size="small" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {offerData && (
          <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              {offerData.image && (
                <Card sx={{ width: 100, height: 100, flexShrink: 0 }}>
                  <CardMedia
                    component="img"
                    sx={{ height: 100, objectFit: 'cover' }}
                    image={offerData.image}
                    alt={offerData.title}
                  />
                </Card>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {offerData.title}
                </Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  ${offerData.price}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Offer Details
        </Typography>

        {trackOfferData ? (
          <Box>
            {/* Offer ID */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Offer ID
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {trackOfferData.orderId ? `#${trackOfferData.orderId}` : ''}
              </Typography>
            </Box>

            {/* Unique ID */}
            {trackOfferData.uniqueId && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Offer Unique ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {trackOfferData.uniqueId}
                </Typography>
              </Box>
            )}

            {/* Seller Info */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Offer From
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {trackOfferData.seller &&
                (trackOfferData.seller.firstName || trackOfferData.seller.lastName)
                  ? `${trackOfferData.seller.firstName || ''} ${
                      trackOfferData.seller.lastName || ''
                    }`.trim()
                  : `User ${trackOfferData.seller?.id}`}
                {trackOfferData.seller?.deletedAt && (
                  <Typography component="span" color="error">
                    {' '}- Deactivated
                  </Typography>
                )}
              </Typography>
            </Box>

            {/* Item Title */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Item
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {trackOfferData.title}
              </Typography>
            </Box>

            {/* Description */}
            {trackOfferData.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Item Description
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {trackOfferData.description}
                </Typography>
              </Box>
            )}

            {/* Price */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Price
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                ${parseFloat(String(trackOfferData.price || 0)).toFixed(2)}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Status */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={trackOfferData.buyerStatus}
                color="primary"
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>

            {/* Payment Status */}
            {trackOfferData.paymentStatus && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Payment Status
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatPaymentStatus(trackOfferData.paymentStatus)}
                </Typography>
              </Box>
            )}

            {/* Next Step with Timer */}
            {trackOfferData.buyerNextStep && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Next Step
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body1" fontWeight="medium">
                    {nextStepMessage.beforeText}
                  </Typography>
                  {nextStepMessage.showTime && (
                    <Chip
                      label={nextStepMessage.timeValue}
                      color={nextStepMessage.timeValue === 'Expired' ? 'error' : 'success'}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Typography variant="body1" fontWeight="medium">
                    {nextStepMessage.afterText}
                  </Typography>
                  {shouldShowExtendTime() && (
                    <Button
                      variant="text"
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => {
                        // Navigate to extend locker time page - to be implemented
                        console.log('Extend locker time');
                      }}
                    >
                      Extend Time
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Locker Info */}
            {trackOfferData.locker && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Locker Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Locker ID
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {trackOfferData.locker.lockerId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {trackOfferData.locker.status}
                    </Typography>
                  </Grid>
                  {trackOfferData.lockerHistory?.bookedAt && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Booked At
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(trackOfferData.lockerHistory.bookedAt).toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                  {trackOfferData.lockerHistory?.timeDuration && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {Math.floor(Number(trackOfferData.lockerHistory.timeDuration))} minutes
                      </Typography>
                    </Grid>
                  )}
                  {trackOfferData.lockerHistory?.timeDuration && trackOfferData.lockerHistory?.bookedAt && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Expires In
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="medium" 
                        color={getTimeRemaining(trackOfferData.lockerHistory.timeDuration, trackOfferData.lockerHistory.bookedAt) === 'Expired' ? 'error.main' : 'inherit'}
                      >
                        {getTimeRemaining(trackOfferData.lockerHistory.timeDuration, trackOfferData.lockerHistory.bookedAt)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {canExtend && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    You can extend locker time
                  </Alert>
                )}
              </>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Proceed to Payment Button */}
            {trackOfferData.status === 'OFFER_SENT' && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleProceedToPayment}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                Proceed to Payment
              </Button>
            )}

            <Divider sx={{ my: 3 }} />

            {isOrderInProgress(trackOfferData.status) && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Amount is held in escrow until this order gets completed and both the parties are satisfied.
              </Alert>
            )}

            {/* Action Buttons */}
            {apiError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {apiError}
              </Alert>
            )}

            {/* Cancel Offer Button */}
            {canCancelOffer(trackOfferData.status) && (
              <Button
                variant="outlined"
                color="error"
                fullWidth
                size="large"
                onClick={handleCancelOffer}
                disabled={loadingCancel}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                {loadingCancel ? <CircularProgress size={24} color="inherit" /> : 'Cancel Offer'}
              </Button>
            )}

            {/* Locker Details Button */}
            {shouldShowLockerDetails() && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => setLockerLocationDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                Locker Details
              </Button>
            )}

            {/* QR Code Button */}
            {shouldShowQRCode() && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => setQrDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                {trackOfferData.status === 'BUYER_UNSATISFIED' ||
                 trackOfferData.status === 'SELLER_EXTENDED_LOCKER_TIME' ||
                 trackOfferData.status === 'SELLER_EXTENDED_LOCKER_TIME_FOR_TAKE_ITEM'
                  ? 'Place Item'
                  : 'Collect Item'}
              </Button>
            )}

            {/* New QR Code Button for BUYER_UNSATISFIED */}
            {shouldShowNewQRCodeButton() && trackOfferData.buyerQrCode && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => setQrDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                {trackOfferData.status === 'BUYER_UNSATISFIED' ||
                 trackOfferData.status === 'SELLER_EXTEND_LOCKER_TIME_FOR_TAKE_ITEM' ||
                 trackOfferData.status === 'SELLER_EXTENDED_LOCKER_TIME_FOR_TAKE_ITEM' ||
                 trackOfferData.status === 'BUYER_EXTENDED_LOCKER_TIME_FOR_PLACE_ITEM'
                  ? 'Place Item'
                  : 'Collect Item'}
              </Button>
            )}

            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleBack}
              sx={{
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              Back
            </Button>
          </Box>
        ) : (
          <Alert severity="info">
            Unable to load offer details. Please try again.
          </Alert>
        )}
      </Paper>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Buyer QR Code</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          {trackOfferData?.buyerQrCode ? (
            <>
              <Box sx={{ background: 'white', p: 2, borderRadius: 2 }}>
                <QRCode 
                  id="buyer-qr-code-svg"
                  value={trackOfferData.buyerQrCode}
                  size={256}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    const svg = document.getElementById("buyer-qr-code-svg");
                    if (!svg) return;
                    
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const img = new Image();
                    img.onload = () => {
                      canvas.width = img.width;
                      canvas.height = img.height;
                      if (ctx) {
                        ctx.fillStyle = "white";
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        ctx.drawImage(img, 0, 0);
                        const pngFile = canvas.toDataURL("image/png");
                        const downloadLink = document.createElement("a");
                        downloadLink.download = "buyer-qrcode.png";
                        downloadLink.href = pngFile;
                        downloadLink.click();
                      }
                    };
                    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
                  }}
                >
                  Download
                </Button>
                {navigator.share && (
                  <Button 
                    variant="outlined"
                    onClick={async () => {
                      const svg = document.getElementById("buyer-qr-code-svg");
                      if (!svg) return;
                      
                      try {
                        const svgData = new XMLSerializer().serializeToString(svg);
                        const canvas = document.createElement("canvas");
                        const ctx = canvas.getContext("2d");
                        const img = new Image();
                        
                        await new Promise((resolve, reject) => {
                          img.onload = resolve;
                          img.onerror = reject;
                          img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
                        });
                        
                        canvas.width = img.width;
                        canvas.height = img.height;
                        if (ctx) {
                          ctx.fillStyle = "white";
                          ctx.fillRect(0, 0, canvas.width, canvas.height);
                          ctx.drawImage(img, 0, 0);
                          canvas.toBlob(async (blob) => {
                            if (!blob) return;
                            const file = new File([blob], 'buyer-qrcode.png', { type: 'image/png' });
                            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                              await navigator.share({
                                files: [file],
                                title: 'Buyer QR Code',
                                text: 'Here is the Buyer QR Code',
                              });
                            } else {
                              console.error('Sharing files is not supported');
                            }
                          }, 'image/png');
                        }
                      } catch (err) {
                        console.error('Share failed', err);
                      }
                    }}
                  >
                    Share
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <Typography>No QR Code available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)} variant="contained" sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Locker Location Dialog */}
      <Dialog open={lockerLocationDialogOpen} onClose={() => setLockerLocationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Locker Location</DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Locker ID:</strong> {trackOfferData?.locker?.lockerId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Location details are available at the container view.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setLockerLocationDialogOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>Close</Button>
          <Button 
            variant="contained" 
            sx={{ textTransform: 'none' }}
            onClick={() => {
              setLockerLocationDialogOpen(false);
              if (trackOfferData?.locker?.containerId) {
                navigate(`/lockers/${trackOfferData.locker.containerId}/view`);
              }
            }}
          >
            View Full Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Offer Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>Cancel Offer</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to cancel this offer? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setCancelDialogOpen(false)} sx={{ textTransform: 'none' }}>No, Keep it</Button>
          <Button 
            color="error" 
            variant="contained"
            disabled={loadingCancel}
            onClick={confirmCancelOffer}
            sx={{ textTransform: 'none' }}
          >
            {loadingCancel ? <CircularProgress size={24} color="inherit" /> : 'Yes, Cancel Offer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BuyerOfferDetail;
